import {
  CableIcon,
  ChromeIcon,
  File01Icon,
  Globe02Icon,
  Mail01Icon,
  RadioIcon,
  Shield01Icon,
  SourceCodeIcon,
  TerminalIcon,
  Upload01Icon,
  Wifi01Icon,
} from '@terra/ui-icons';
import {
  BROWSER_AUTOMATION_TOOL_NAME,
  INTERCEPT_RULE_TOOL_NAME,
  INTERCEPT_TOGGLE_TOOL_NAME,
  LAUNCH_BROWSER_TOOL_NAME,
  MATCH_REPLACE_RULE_TOOL_NAME,
  PROXY_CONTROL_TOOL_NAME,
  PROXY_HISTORY_TOOL_NAME,
  REPLAY_REQUEST_TOOL_NAME,
  TERMINAL_TOOL_NAMES,
  TERMINAL_TOOL_PRESENTATION,
  TRACK_HISTORY_REQUEST_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import {
  createPortalToolConfig,
  formatHttpExecutorPayload,
  formatHttpPayload,
  formatPythonPayload,
  formatTerminalPayload,
  httpHitlConfig,
  renderBrowserResult,
  renderCoderCard,
  renderEmailInboxCard,
  renderHitlExecutorContent,
  renderHttpCard,
  renderHttpResult,
  renderInteractshCard,
  renderPythonCard,
  renderPythonResult,
  renderReplayRequestCard,
  renderSseClientCard,
  renderSseClientResult,
  renderTerminalResult,
  renderTrackHistoryRequestCard,
  renderTrackHistoryRequestHitlContent,
  renderValidatedHttpResult,
  renderWebSocketClientCard,
  renderWebSocketClientResult,
  resolveTrackHistoryRequestHitlApprovalBlocked,
} from './tools';
import { extractTerminalMetadata } from './tools/terminal';
import {
  extractAuthenticationDetailsMetadata,
  extractHttpCredentialId,
  extractHttpExecutorMetadata,
  extractHttpMetadata,
  extractInteractshMetadata,
  extractLoginCredentialId,
  extractLoginMetadata,
  extractPythonMetadata,
  extractSseClientMetadata,
  extractWebSocketClientMetadata,
  hasAuthenticationDetailsExpandableContent,
  hasLoginExpandableContent,
} from './extractors';
import { getTerminalDisplayInput, parseTerminalOutput } from './parsers';
import {
  getHttpCustomActions,
  getInteractshCustomActions,
  getPythonCustomActions,
  getSseClientCustomActions,
  getWebSocketClientCustomActions,
} from './actions';
import {
  renderBrowserStreamingContent,
  renderDefaultEditContent,
  renderDefaultEditModal,
  renderHttpContent,
  renderTerminalCard,
  renderTerminalContent,
} from './renderers';
import { renderCoderHitlContent, renderCoderResult } from './tools/coder';
import { renderJwtResult } from './tools/jwt';
import { renderInteractshResult } from './tools/interactsh';
import { renderAuthenticateChatRow } from './tools/authenticate/authenticate-chat-row';
import { renderLoginResult } from './tools/login';
import { renderAuthenticationDetailsResult } from './tools/authentication-details';
import type { ToolConfig } from './tool-config-types';

const buildTerminalToolConfigs = (): Record<string, ToolConfig> =>
  Object.fromEntries(
    TERMINAL_TOOL_NAMES.map((toolName) => {
      const presentation = TERMINAL_TOOL_PRESENTATION[toolName];

      return [
        toolName,
        {
          name: presentation.name,
          icon: TerminalIcon,
          category: 'executable',
          cardType: 'minimal',
          contentType: 'terminal',
          usesInlineEdit: true,
          rendersApprovalFooter: true,
          hitlConfig: httpHitlConfig,
          runningText: presentation.runningText,
          renderContent: renderTerminalContent,
          renderOutput: renderTerminalResult,
          formatPayload: formatTerminalPayload,
          parseOutput: parseTerminalOutput,
          getDisplayInput: getTerminalDisplayInput,
          extractMetadata: extractTerminalMetadata,
          renderCard: renderTerminalCard,
        } satisfies ToolConfig,
      ];
    }),
  );

export const buildExecutableToolConfigs = (): Record<string, ToolConfig> => ({
  'http-tool': {
    name: 'HTTP Request',
    icon: Globe02Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'http',
    usesInlineEdit: true,
    rendersFailureCard: true,
    hitlConfig: httpHitlConfig,
    renderCard: renderHttpCard,
    renderContent: renderHttpContent,
    renderOutput: renderHttpResult,
    formatPayload: formatHttpPayload,
    extractCredentialId: extractHttpCredentialId,
    extractMetadata: extractHttpMetadata,
    getCustomActions: getHttpCustomActions,
  },
  'dynamic-http-tool': {
    name: 'HTTP Request',
    icon: Globe02Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'http',
    usesInlineEdit: true,
    rendersFailureCard: true,
    hitlConfig: httpHitlConfig,
    renderCard: renderHttpCard,
    renderContent: renderHttpContent,
    renderOutput: renderHttpResult,
    formatPayload: formatHttpPayload,
    extractCredentialId: extractHttpCredentialId,
    extractMetadata: extractHttpMetadata,
    getCustomActions: getHttpCustomActions,
  },
  'validated-http-tool': {
    name: 'HTTP Request',
    icon: Globe02Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'validated-http',
    rendersFailureCard: true,
    renderCard: renderHttpCard,
    renderOutput: renderValidatedHttpResult,
    formatPayload: formatHttpPayload,
    extractCredentialId: extractHttpCredentialId,
    extractMetadata: extractHttpMetadata,
    getCustomActions: getHttpCustomActions,
  },
  'http-payload-creator': {
    name: 'Draft HTTP Request',
    icon: Globe02Icon,
    category: 'executable',
    cardType: 'minimal',
    renderCard: () => null,
  },
  'http-executor': {
    name: 'Send HTTP Request',
    icon: Globe02Icon,
    category: 'executable',
    cardType: 'minimal',
    rendersFailureCard: true,
    hitlConfig: httpHitlConfig,
    renderCard: renderHttpCard,
    renderContent: renderHitlExecutorContent,
    formatPayload: formatHttpExecutorPayload,
    renderOutput: renderValidatedHttpResult,
    extractCredentialId: extractHttpCredentialId,
    extractMetadata: extractHttpExecutorMetadata,
    getCustomActions: getHttpCustomActions,
  },
  ...buildTerminalToolConfigs(),
  [PROXY_CONTROL_TOOL_NAME]: createPortalToolConfig(PROXY_CONTROL_TOOL_NAME),
  [INTERCEPT_TOGGLE_TOOL_NAME]: createPortalToolConfig(
    INTERCEPT_TOGGLE_TOOL_NAME,
  ),
  [INTERCEPT_RULE_TOOL_NAME]: createPortalToolConfig(INTERCEPT_RULE_TOOL_NAME),
  [MATCH_REPLACE_RULE_TOOL_NAME]: createPortalToolConfig(
    MATCH_REPLACE_RULE_TOOL_NAME,
  ),
  [REPLAY_REQUEST_TOOL_NAME]: {
    ...createPortalToolConfig(REPLAY_REQUEST_TOOL_NAME),
    renderCard: renderReplayRequestCard,
  },
  [LAUNCH_BROWSER_TOOL_NAME]: createPortalToolConfig(LAUNCH_BROWSER_TOOL_NAME),
  [BROWSER_AUTOMATION_TOOL_NAME]: createPortalToolConfig(
    BROWSER_AUTOMATION_TOOL_NAME,
  ),
  [PROXY_HISTORY_TOOL_NAME]: createPortalToolConfig(PROXY_HISTORY_TOOL_NAME),
  [TRACK_HISTORY_REQUEST_TOOL_NAME]: {
    ...createPortalToolConfig(TRACK_HISTORY_REQUEST_TOOL_NAME),
    hitlConfig: httpHitlConfig,
    resolveHitlApprovalBlocked: resolveTrackHistoryRequestHitlApprovalBlocked,
    renderContent: renderTrackHistoryRequestHitlContent,
    renderCard: renderTrackHistoryRequestCard,
  },
  'python-tool': {
    name: 'Python',
    runningText: 'Running Python...',
    icon: SourceCodeIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'python',
    supportsHttpTimeline: true,
    harToolType: 'PYTHON',
    renderCard: renderPythonCard,
    renderOutput: renderPythonResult,
    formatPayload: formatPythonPayload,
    extractMetadata: extractPythonMetadata,
    getCustomActions: getPythonCustomActions,
  },
  'coder-tool': {
    name: 'Code Runner',
    icon: SourceCodeIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'coder',
    supportsHttpTimeline: true,
    harToolType: 'PYTHON',
    hideInput: true,
    runningText: 'Writing and running code...',
    renderCard: renderCoderCard,
    renderContent: renderCoderHitlContent,
    renderOutput: renderCoderResult,
    formatPayload: formatPythonPayload,
    getCustomActions: getPythonCustomActions,
    extractMetadata: extractPythonMetadata,
  },
  'file-upload-client': {
    name: 'File Upload Tool',
    icon: Upload01Icon,
    category: 'executable',
    cardType: 'minimal',
    supportsHttpTimeline: true,
    harToolType: 'HTTP',
    runningText: 'Uploading file...',
  },
  'prepare-file': {
    name: 'Prepare File',
    icon: File01Icon,
    category: 'executable',
    cardType: 'minimal',
    runningText: 'Preparing file...',
  },
  read_emails: {
    name: 'Read Emails',
    runningText: 'Reading emails...',
    icon: Mail01Icon,
    category: 'intelligence',
    cardType: 'minimal',
  },
  'read-emails': {
    name: 'Read Emails',
    runningText: 'Reading emails...',
    icon: Mail01Icon,
    category: 'intelligence',
    cardType: 'minimal',
  },
  tag_emails: {
    name: 'Tag Emails',
    runningText: 'Tagging emails...',
    icon: Mail01Icon,
    category: 'intelligence',
    cardType: 'minimal',
  },
  'tag-emails': {
    name: 'Tag Emails',
    runningText: 'Tagging emails...',
    icon: Mail01Icon,
    category: 'intelligence',
    cardType: 'minimal',
  },
  save_relevant_emails: {
    name: 'Read Email Inbox',
    runningText: 'Saving relevant emails...',
    icon: Mail01Icon,
    category: 'intelligence',
    cardType: 'minimal',
    renderCard: renderEmailInboxCard,
  },
  'save-relevant-emails': {
    name: 'Read Email Inbox',
    runningText: 'Saving relevant emails...',
    icon: Mail01Icon,
    category: 'intelligence',
    cardType: 'minimal',
    renderCard: renderEmailInboxCard,
  },
  'headless-browser-tool': {
    name: 'Browser',
    icon: ChromeIcon,
    category: 'executable',
    cardType: 'minimal',
  },
  'browser-tool': {
    name: 'Browser',
    icon: ChromeIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'browser',
    renderOutput: renderBrowserResult,
  },
  'ai-browser-tool': {
    name: 'Browse',
    icon: ChromeIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'browser',
    isStreaming: true,
    showsTaskDescription: true,
    runningText: 'Navigating the browser...',
    renderStreamingContent: renderBrowserStreamingContent,
    renderOutput: renderBrowserResult,
  },
  authenticate: {
    name: 'Authenticate',
    icon: Shield01Icon,
    category: 'executable',
    cardType: 'inline',
    hideInput: true,
    runningText: 'Authenticating credential...',
    renderCard: renderAuthenticateChatRow,
    hasExpandableContent: () => false,
  },
  'login-tool': {
    name: 'Login',
    icon: Shield01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'login',
    isStreaming: true,
    hideInput: true,
    runningText: 'Logging in...',
    renderStreamingContent: renderBrowserStreamingContent,
    renderOutput: renderLoginResult,
    extractCredentialId: extractLoginCredentialId,
    extractMetadata: extractLoginMetadata,
    hasExpandableContent: hasLoginExpandableContent,
  },
  'get-authentication-details': {
    name: 'Authenticate',
    icon: Shield01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'authentication-details',
    isStreaming: true,
    hideInput: true,
    runningText: 'Authenticating...',
    renderStreamingContent: renderBrowserStreamingContent,
    renderOutput: renderAuthenticationDetailsResult,
    extractMetadata: extractAuthenticationDetailsMetadata,
    hasExpandableContent: hasAuthenticationDetailsExpandableContent,
  },
  'jwt-tool': {
    name: 'JWT',
    icon: Shield01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'jwt',
    renderOutput: renderJwtResult,
    renderEditModal: renderDefaultEditModal,
    renderEditContent: renderDefaultEditContent,
    usesTabEdit: true,
  },
  'interactsh-tool': {
    name: 'Out-of-Band Probe',
    icon: RadioIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'interactsh',
    renderCard: renderInteractshCard,
    renderOutput: renderInteractshResult,
    renderEditModal: renderDefaultEditModal,
    renderEditContent: renderDefaultEditContent,
    usesTabEdit: true,
    extractMetadata: extractInteractshMetadata,
    getCustomActions: getInteractshCustomActions,
  },
  'sse-client-tool': {
    name: 'Server-Sent Events',
    icon: Wifi01Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'sse-client',
    renderCard: renderSseClientCard,
    renderOutput: renderSseClientResult,
    extractMetadata: extractSseClientMetadata,
    getCustomActions: getSseClientCustomActions,
  },
  'websocket-client-tool': {
    name: 'WebSocket',
    icon: CableIcon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'websocket-client',
    renderCard: renderWebSocketClientCard,
    renderOutput: renderWebSocketClientResult,
    extractMetadata: extractWebSocketClientMetadata,
    getCustomActions: getWebSocketClientCustomActions,
  },
});
